import styled from '@emotion/styled';
import { ResizeObserver } from '@juggle/resize-observer';
import {
  Row,
  Column,
  device,
  typography,
  mobileGutterMargin,
} from '@landsbankinn/ui';
import React, { useEffect, useState } from 'react';

import { animated, useSpring, useTransition } from 'react-spring';
import useMeasure from 'react-use-measure';
import { Chapter } from './Chapter';
import { NavItem } from './NavItem';

interface ChapterItem {
  id: string;
  title: string | null;
  content: React.ReactNode;
}

interface IProps {
  heading: string | null;
  chapters: ChapterItem[];
}

const Host = styled(animated.div)`
  margin-left: ${(props) => `-${props.theme.containerGutter}px`};
  margin-right: ${(props) => `-${props.theme.containerGutter}px`};
  padding: 50px;
  padding: ${(props) =>
    `${props.theme.breakpoints.sm.gutter * 2}px ${
      props.theme.breakpoints.sm.gutter
    }px`};
  background-color: ${(props) => props.theme.colors.backgroundAlt};

  ${mobileGutterMargin}

  @media ${device.tablet} {
    margin: 0;
    padding: 50px;
  }
`;

const Wrap = styled.div`
  display: none;

  @media ${device.tablet} {
    display: flex;
    flex-flow: column nowrap;
    justify-content: space-between;

    position: relative;
    height: 100%;
  }
`;

const TitleWrap = styled.div`
  ${typography.h3}
  position: relative;
  margin-bottom: 30px;
  overflow: hidden;
  @media ${device.tablet} {
    margin-bottom: 300px;

    position: sticky;
    top: 160px;
  }
`;

const Title = styled.h2`
  ${typography.h2}

  padding-bottom: 30px;
  border-bottom: ${(props) => `1px solid ${props.theme.colors.primary}`};

  @media ${device.tablet} {
    margin-bottom: 90px;
  }
`;

const Heading = styled(animated.h2)`
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
`;

const Nav = styled.nav`
  @media ${device.tablet} {
    display: block;
    position: sticky;
    bottom: 50px;
    margin-top: 300px;
    border-left: 2px solid ${(props) => props.theme.shades.primary2};
  }
`;

export const ContentNav = ({
  heading,
  chapters,
}: // headings,
// children,
// activeIndex,
// nav,
IProps) => {
  const [copyRef, copySize] = useMeasure({ polyfill: ResizeObserver });
  const [activeId, setActiveId] = useState(chapters?.[0]?.id);
  const transition = useTransition(activeId, {
    from: { y: 30, opacity: 1 },
    enter: { y: 0, opacity: 1 },
    leave: { y: -30, opacity: 0 },
  });

  const [_, spring] = useSpring(() => ({
    y: 0,
    onChange: (props) => {
      window.scrollTo(0, props.value.y);
    },
  }));

  const onChapterChange = (id: string) => {
    setActiveId(id);
  };

  useEffect(() => {
    const onScroll = () => spring.stop();
    window.addEventListener('wheel', onScroll, false);
    return () => {
      window.removeEventListener('wheel', onScroll, false);
    };
  }, []);

  return (
    <Host>
      <Title>{heading}</Title>
      <Row>
        <Column md={{ width: 5 / 12 }}>
          <Wrap>
            <TitleWrap style={{ height: `${copySize.height}px` }}>
              {transition((style) => {
                return (
                  <Heading ref={copyRef} style={style as any}>
                    {chapters.find((c) => c.id === activeId)?.title}
                  </Heading>
                );
              })}
            </TitleWrap>
            <Nav>
              {chapters.map((chapter) => (
                <NavItem
                  href={`#${chapter.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    const offset = 160;
                    const el = document.getElementById(chapter.id);
                    if (el) {
                      const y =
                        el.getBoundingClientRect().top +
                        window.scrollY -
                        offset;
                      spring.update({
                        y,
                        reset: true,
                        from: { y: window.scrollY },
                      });
                    }
                    return false;
                  }}
                  isActive={chapter.id === activeId}
                  key={chapter.id}
                >
                  {chapter.title ?? ''}
                </NavItem>
              ))}
            </Nav>
          </Wrap>
        </Column>
        <Column md={{ width: 6 / 12, offset: 1 / 12 }}>
          {chapters.map((chapter) => (
            <Chapter
              key={chapter.id}
              id={chapter.id}
              subHeading={chapter.title}
              onChange={onChapterChange}
            >
              {chapter.content}
            </Chapter>
          ))}
        </Column>
      </Row>
    </Host>
  );
};
